import { useEffect, useState } from "react";
import API from "../../api/api";
import Modal from "../../components/Modal";
import Toast from "../../components/Toast";
import FormInput from "../../components/FormInput";

function MembershipList() {
    const [list, setList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [toast, setToast] = useState(null);
    const [membershipTypes, setMembershipTypes] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        membershipTypeId: "",
        firstName: "",
        lastName: "",
        mobile: "",
        email: "",
        address: "",
        spouseName: "",
        child1: "",
        child2: "",
        child3: "",
        child4: "",
        donationAmount: 0
    });

    const fetchMemberships = () => {
        setLoading(true);
        console.log("Fetching memberships from /membership...");
        API.get("/membership")
            .then(res => {
                console.log("Memberships received:", res.data);
                setList(res.data);
                setFilteredList(res.data);
            })
            .catch(err => {
                console.error("Error fetching memberships:", err);
                console.error("Error details:", err.response?.data || err.message);
                setList([]);
            })
            .finally(() => setLoading(false));
    };

    const fetchMembershipTypes = async () => {
        try {
            const res = await API.get("/membership-types");
            setMembershipTypes(res.data);
        } catch (err) {
            console.error("Error fetching membership types:", err);
        }
    };

    useEffect(() => {
        fetchMemberships();
        fetchMembershipTypes();
    }, []);

    useEffect(() => {
        const results = list.filter(x =>
            `${x.FirstName} ${x.LastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            x.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            x.Mobile?.includes(searchTerm)
        );
        setFilteredList(results);
    }, [searchTerm, list]);

    const handleOpenModal = () => {
        setFormData({
            membershipTypeId: "",
            firstName: "",
            lastName: "",
            mobile: "",
            email: "",
            address: "",
            spouseName: "",
            child1: "",
            child2: "",
            child3: "",
            child4: "",
            donationAmount: 0
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            console.log("Submitting Membership Payload:", formData);
            const params = new URLSearchParams(formData).toString();
            await API.post(`/membership?${params}`);
            showToast("Membership submitted successfully");
            setIsModalOpen(false);
            fetchMemberships();
        } catch (err) {
            console.error("Error creating membership:", err);
            showToast("Error creating membership", "error");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete this membership record?")) {
            try {
                await API.delete(`/membership/${id}`);
                showToast("Record deleted");
                fetchMemberships();
            } catch (err) {
                showToast("Error deleting record", "error");
            }
        }
    };

    const showToast = (message, type = "success") => {
        setToast({ message, type });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid': return { bg: '#dcfce7', text: '#15803d' };
            case 'pending': return { bg: '#fef9c3', text: '#a16207' };
            case 'failed': return { bg: '#fee2e2', text: '#b91c1c' };
            default: return { bg: '#f1f5f9', text: '#475569' };
        }
    };

    return (
        <div className="admin-card animate-fade-in">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px", flexWrap: "wrap", gap: "20px" }}>
                <div>
                    <h2 style={{ margin: 0, color: "var(--text-dark)", fontSize: "1.75rem" }}>Membership Applications</h2>
                    <p style={{ margin: "5px 0 0 0", color: "var(--text-muted)", fontSize: "0.9rem" }}>Manage and review all community membership applications</p>
                </div>
                <div style={{ display: "flex", gap: "15px", alignItems: "center", flex: 1, justifyContent: "flex-end" }}>
                    <div className="search-container">
                        <span className="search-icon">🔍</span>
                        <input
                            className="form-control"
                            placeholder="Search by name, email, mobile..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="btn btn-primary" onClick={handleOpenModal} style={{ height: "45px" }}>
                        <span>+</span> Create New Member
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: "100px" }}>
                    <div className="spinner"></div>
                    <p style={{ marginTop: "15px", color: "var(--text-muted)" }}>Fetching memberships...</p>
                </div>
            ) : (
                <div className="member-card-grid">
                    {filteredList.map(x => {
                        const status = getStatusColor(x.PaymentStatus);
                        const typeName = membershipTypes.find(t => t.MembershipTypeId === x.MembershipTypeId)?.TypeName || x.MembershipTypeName || 'N/A';
                        
                        const children = [x.Child1, x.Child2, x.Child3, x.Child4].filter(Boolean);

                        return (
                            <div key={x.MembershipId} className="member-card">
                                <div className="member-card-header">
                                    <div className="member-info">
                                        <div className="member-id">#{x.MembershipId}</div>
                                        <h3>{x.FirstName} {x.LastName}</h3>
                                        <span className="badge" style={{ backgroundColor: status.bg, color: status.text, alignSelf: "flex-start", marginTop: "4px" }}>
                                            {x.PaymentStatus || 'PENDING'}
                                        </span>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                        <div className="badge" style={{ background: "var(--primary-light)", color: "#fff" }}>{typeName}</div>
                                    </div>
                                </div>

                                <div className="member-card-body">
                                    <div className="info-row">
                                        <span className="info-label">📧 Email</span>
                                        <span className="info-value" title={x.Email}>{x.Email}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">📱 Mobile</span>
                                        <span className="info-value">{x.Mobile}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">📍 Address</span>
                                        <span className="info-value" title={x.Address}>{x.Address}</span>
                                    </div>

                                    {(x.SpouseName || children.length > 0) && (
                                        <div className="family-info">
                                            <span className="family-title">Family Details</span>
                                            {x.SpouseName && (
                                                <div className="info-row" style={{ marginBottom: "8px" }}>
                                                    <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Spouse:</span>

                                                    <span style={{ fontSize: "0.85rem", fontWeight: "600" }}>{x.SpouseName}</span>
                                                </div>
                                            )}
                                            {children.length > 0 && (
                                                <div className="children-list">
                                                    {children.map((child, idx) => (
                                                        <span key={idx} className="child-tag">👶 {child}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="member-card-footer">
                                    <div className="financials">
                                        <span className="total-amt">${x.TotalAmount}</span>
                                        {x.DonationAmount > 0 && (
                                            <span className="donation-amt">incl. ${x.DonationAmount} donation</span>
                                        )}
                                    </div>
                                    <div className="card-actions">
                                        <button 
                                            className="btn btn-outline" 
                                            onClick={() => handleDelete(x.MembershipId)} 
                                            style={{ padding: "6px 12px", color: "var(--danger)", borderColor: "rgba(239, 68, 68, 0.2)" }}
                                        >
                                            🗑️ Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {filteredList.length === 0 && !loading && (
                <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                    No membership applications found.
                </div>
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* Create Member Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Create New Membership"
            >
                <form onSubmit={handleSubmit} className="premium-form">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                        <div className="form-group">
                            <label>Membership Type</label>
                            <select
                                className="form-control"
                                value={formData.membershipTypeId}
                                onChange={e => setFormData({ ...formData, membershipTypeId: e.target.value })}
                                required
                                style={{ padding: '10px' }}
                            >
                                <option value="">Select Type</option>
                                {membershipTypes.map(t => (
                                    <option key={t.MembershipTypeId} value={t.MembershipTypeId}>{t.TypeName}</option>
                                ))}
                            </select>
                        </div>
                        <FormInput label="First Name" value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} required />
                        <FormInput label="Last Name" value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} required />
                        <FormInput label="Mobile" value={formData.mobile} onChange={e => setFormData({ ...formData, mobile: e.target.value })} required />
                        <FormInput label="Email" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                        <FormInput label="Address" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} required />
                        <FormInput label="Spouse Name" value={formData.spouseName} onChange={e => setFormData({ ...formData, spouseName: e.target.value })} />
                        <FormInput label="Child 1" value={formData.child1} onChange={e => setFormData({ ...formData, child1: e.target.value })} />
                        <FormInput label="Child 2" value={formData.child2} onChange={e => setFormData({ ...formData, child2: e.target.value })} />
                        <FormInput label="Child 3" value={formData.child3} onChange={e => setFormData({ ...formData, child3: e.target.value })} />
                        <FormInput label="Child 4" value={formData.child4} onChange={e => setFormData({ ...formData, child4: e.target.value })} />
                        <FormInput label="Donation Amount" type="number" value={formData.donationAmount} onChange={e => setFormData({ ...formData, donationAmount: e.target.value })} />
                    </div>

                    <div style={{ marginTop: "30px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                        <button type="button" className="btn btn-outline" onClick={() => setIsModalOpen(false)}>Cancel</button>
                        <button type="submit" className="btn btn-primary">Create Membership</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

export default MembershipList;